import { categoryPromptBlueprints } from "@/data/promptInstructions";
import { getPrimaryUserInput, hasMeaningfulUserInput, normalizeFinnishPromptText, normalizeUserInputToEnglish, sanitizeEnglishPrompt } from "@/lib/languageNormalizer";
import { appendFinnishVisualGuidance } from "@/lib/visualPromptDefaults";

const clean = (value, fallback) => (hasMeaningfulUserInput(value) ? String(value).trim() : fallback);

const cleanEnglish = (value, fallback, context = "generic") => normalizeUserInputToEnglish(clean(value, fallback), context);

const cleanFinnish = (value, fallback) => normalizeFinnishPromptText(clean(value, fallback));

const optionalEnglish = (value, context = "generic") => (hasMeaningfulUserInput(value) ? normalizeUserInputToEnglish(value, context) : "");

const optionalFinnish = (value) => (hasMeaningfulUserInput(value) ? normalizeFinnishPromptText(value) : "");

const sanitizePromptBlock = (input) =>
  String(input || "")
    .split("\n")
    .map((line) => sanitizeEnglishPrompt(line))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const stripGoalPrefix = (value) =>
  String(value || "")
    .replace(/^the goal is to\s+/i, "to ")
    .replace(/^the goal is\s+/i, "")
    .trim();

const toneSentence = (tone) => {
  if (!tone) return "Use a natural, clear and human-sounding tone.";
  if (/^[aeiou]/i.test(tone)) return `Use an ${tone} tone.`;
  return `Use a ${tone} tone.`;
};

const textRestrictionBlock = (customAvoid) => [
  "avoid overly promotional or corporate language",
  "avoid sounding like AI-generated text",
  "avoid overly polished, generic or vague output",
  "avoid difficult jargon and unnecessarily long sentences",
  normalizeUserInputToEnglish(clean(customAvoid, "avoid vague instructions, overly technical language and content that does not help the user in practice"), "avoid"),
]
  .map((item) => `- ${item}`)
  .join("\n");

const visualRestrictionBlock = (customAvoid) => [
  "avoid plastic-looking skin, over-smoothed faces and unrealistic beauty retouching",
  "avoid distorted hands, eyes, hair, teeth, facial expressions and body proportions",
  "avoid an overly polished AI look, fake studio perfection and unrealistic details",
  "avoid changing identity, recognizable features, clothing, product shape or important background details",
  "avoid flicker, morphing, extra limbs, text artifacts, logo distortion and unrealistic motion",
  normalizeUserInputToEnglish(clean(customAvoid, "avoid generic stock-photo feeling, unrealistic perfection and details that do not fit the real-life context"), "avoid"),
]
  .map((item) => `- ${item}`)
  .join("\n");

const textRestrictionBlockFi = (customAvoid) => [
  "vältä liian mainosmaista tai korporaatiomaista kieltä",
  "vältä tekoälymäistä, geneeristä tai ylisiloteltua ilmaisua",
  "vältä liian pitkiä ja vaikeasti ymmärrettäviä lauseita",
  normalizeFinnishPromptText(clean(customAvoid, "vältä epämääräisiä ohjeita ja liian teknistä kieltä")),
]
  .map((item) => `- ${item}`)
  .join("\n");

const visualRestrictionBlockFi = (customAvoid) => [
  "vältä muovista ihoa, ylisiloteltuja kasvoja ja epärealistista beauty filter -ilmettä",
  "vältä vääristyneitä käsiä, silmiä, hiuksia, hampaita, ilmeitä ja mittasuhteita",
  "vältä liian täydellistä tai selvästi tekoälymäistä lopputulosta",
  "vältä identiteetin, vaatetuksen, tuotteen, taustan tai tärkeiden yksityiskohtien muuttumista",
  normalizeFinnishPromptText(clean(customAvoid, "vältä geneeristä stock photo -tuntua ja epärealistista täydellisyyttä")),
]
  .map((item) => `- ${item}`)
  .join("\n");

export const initialGeneratorForm = {
  promptType: "somepostaus",
  topic: "",
  audience: "",
  goal: "",
  tone: "",
  channel: "",
  length: "",
  outcome: "",
  avoid: "",
  voice: "",
  notes: "",
};

export const buildGeneralPrompt = (form) => {
  const blueprint = categoryPromptBlueprints[form.promptType] || categoryPromptBlueprints.somepostaus;
  const isVisual = form.promptType === "ai-kuva" || form.promptType === "ai-video";
  const topicFi = cleanFinnish(form.topic, "selkeä konkreettinen aihe");
  const goalFi = optionalFinnish(form.goal);
  const audienceFi = optionalFinnish(form.audience);
  const toneFi = optionalFinnish(form.tone);
  const channelFi = optionalFinnish(form.channel);
  const lengthFi = optionalFinnish(form.length);
  const outcomeFi = optionalFinnish(form.outcome);
  const voiceFi = optionalFinnish(form.voice);
  const notesFi = optionalFinnish(form.notes);

  if (isVisual) {
    const visualPrompt = `Rooli:
Toimi kokeneena ${form.promptType === "ai-video" ? "AI-videopromptien" : "AI-kuvien"} suunnittelijana ja realistisen visuaalisen sisällön asiantuntijana.

Tavoite:
Luo selkeä suomenkielinen ${form.promptType === "ai-video" ? "videoprompti" : "kuvaprompti"} aiheesta: ${topicFi}.${goalFi ? ` Tavoitteena on ${goalFi}.` : ""}

Konteksti:
${[
  `Aihe: ${topicFi}`,
  channelFi ? `Kanava tai käyttötarkoitus: ${channelFi}` : null,
  audienceFi ? `Kohderyhmä: ${audienceFi}` : null,
  voiceFi ? `Brändin tai tekijän ääni: ${voiceFi}` : null,
  outcomeFi ? `Haluttu lopputulos: ${outcomeFi}` : null,
  lengthFi ? `Pituus tai rajaus: ${lengthFi}` : null,
  notesFi ? `Erityishuomiot: ${notesFi}` : null,
].filter(Boolean).join("\n")}

Tyyli ja laatu:
${toneFi ? `Käytä ${toneFi}a sävyä.` : "Käytä luonnollista, selkeää ja ihmisen kuuloista sävyä."} Lopputuloksen pitää tuntua aidolta, realistiselta ja käyttövalmiilta, ei geneeriseltä tai tekoälymäiseltä.

Rajoitteet:
${visualRestrictionBlockFi(form.avoid)}

Lopputulos:
Tuota valmis suomenkielinen ${form.promptType === "ai-video" ? "videoprompti" : "kuvaprompti"}, joka on suoraan kopioitavissa. Älä lisää tyhjiä hakasulkeita tai englanninkielistä promptirunkoa.`;

    return appendFinnishVisualGuidance(visualPrompt);
  }

  return `Rooli:
${blueprint.role || "Toimi kokeneena suomenkielisenä promptisuunnittelijana."}

Tavoite:
Luo käyttövalmis ${blueprint.label || "prompti"} aiheesta: ${topicFi}.${goalFi ? ` Tavoitteena on ${goalFi}.` : ""}

Konteksti:
${[
  `Aihe: ${topicFi}`,
  channelFi ? `Kanava tai käyttötarkoitus: ${channelFi}` : null,
  voiceFi ? `Brändin tai tekijän ääni: ${voiceFi}` : null,
  outcomeFi ? `Haluttu lopputulos: ${outcomeFi}` : null,
  lengthFi ? `Pituus tai rajaus: ${lengthFi}` : null,
  notesFi ? `Erityishuomiot: ${notesFi}` : null,
].filter(Boolean).join("\n")}

Kohderyhmä:
${audienceFi || "Käytä aiheen ja kontekstin perusteella luontevasti pääteltävää kohderyhmää."}

Tyyli ja sävy:
${toneFi ? `Käytä ${toneFi}a sävyä.` : "Käytä luonnollista, selkeää ja ihmisen kuuloista sävyä."} Lopputuloksen pitää tuntua lämpimältä, selkeältä, käytännölliseltä ja helposti lähestyttävältä.

Laatuperiaate:
Lopputuloksen pitää tuntua aidolta, luonnolliselta, uskottavalta ja käytännönläheiseltä. Lisää konkreettisia arjen yksityiskohtia ja inhimillistä rytmiä silloin, kun se auttaa.

Rakenne:
${blueprint.structure || "Luo selkeä rakenne, joka sopii valittuun sisältötyyppiin ja on aloittelijalle helppo käyttää."}

Formaatti:
${blueprint.format || "Kirjoita valmis prompti suomeksi, konkreettisesti ja helposti muokattavaksi."}

Rajoitteet:
${textRestrictionBlockFi(form.avoid)}

Esimerkit ja erityishuomiot:
${notesFi || "Tee maltillisia oletuksia vain silloin, kun tieto puuttuu, mutta pidä käyttäjän aihe ja tavoite pääsisältönä."}

Lopputulos:
Tuota valmis ${blueprint.label || "prompti"} suomeksi. Älä lisää tyhjiä kohtia, hakasulkeita tai englanninkielistä promptirunkoa.

Luo prompti nyt konkreettiseksi, aloittelijaystävälliseksi ja suoraan käytettäväksi.`;
};

export const initialImproverForm = { originalPrompt: "", targetTool: "ChatGPT", goal: "", audience: "", tone: "" };

export const improvePrompt = (form) => {
  const originalPrompt = cleanFinnish(form.originalPrompt, "liitä alkuperäinen prompti tähän");
  const targetTool = cleanFinnish(form.targetTool, "valittu AI-työkalu");
  const goal = cleanFinnish(form.goal, "saada selkeämpi, tarkempi ja hyödyllisempi vastaus");
  const audience = cleanFinnish(form.audience, "kohderyhmä tai käyttötilanne");
  const tone = cleanFinnish(form.tone, "selkeä, käytännönläheinen, lämmin ja luonnollinen");

  return `Rooli:
Toimi käytännönläheisenä promptivalmentajana, joka muuttaa keskeneräiset ideat selkeiksi, konkreettisiksi ja helposti käytettäviksi prompteiksi.

Tavoite:
Paranna käyttäjän promptia niin, että ${targetTool} voi tuottaa selkeän, konkreettisen ja käyttövalmiin lopputuloksen. Tavoitteena on ${goal}.

Alkuperäinen prompti:
"""
${originalPrompt}
"""

Konteksti ja kohderyhmä:
${audience}

Tyyli ja sävy:
Kirjoita ${tone}sti. Vältä liian mainosmaista, geneeristä tai tekoälymäistä ilmaisua.

Laatuperiaate:
Parannetun promptin pitää ohjata mallia tuottamaan aidolta, luonnolliselta, uskottavalta ja käytännölliseltä tuntuva vastaus.

Rakenne:
Kirjoita parannettu prompti selkeällä rakenteella:
1. Rooli: mitä roolia AI:n pitää ottaa
2. Tavoite: mitä AI:n pitää tuottaa
3. Konteksti: tärkeät taustatiedot
4. Kohderyhmä: kenelle lopputulos tehdään
5. Tyyli ja sävy: miltä vastauksen pitää kuulostaa
6. Rakenne: miten sisältö järjestetään
7. Formaatti: missä muodossa vastaus annetaan
8. Rajoitteet: mitä pitää välttää ja mitä on pakko huomioida
9. Lopputulos: selkeä pyyntö valmiista vastauksesta

Rajoitteet:
${textRestrictionBlockFi("älä tee promptista liian pitkää, sekavaa tai epämääräistä")}

Lopputulos:
Tuota parannettu prompti suomeksi, valmiina kopioitavaksi ${targetTool}-työkaluun. Lisää loppuun lyhyt 5 kohdan tarkistuslista promptin laadun arviointiin.`;
};

export const initialImageForm = {
  subject: "",
  environment: "",
  lighting: "",
  camera: "",
  angle: "",
  realism: "",
  mood: "",
  clothing: "",
  colors: "",
  negative: "",
};

export const buildImagePrompt = (form) => {
  const primaryInput = getPrimaryUserInput(form, ["subject", "environment", "mood", "clothing"], false);
  const subject = cleanFinnish(form.subject || primaryInput, "pääkohde");
  const environment = cleanFinnish(form.environment, "aito, eletty ympäristö");
  const lighting = cleanFinnish(form.lighting, "pehmeä luonnonvalo ikkunasta");
  const camera = cleanFinnish(form.camera, "luonnollinen dokumentaarinen kameratyyli");
  const angle = cleanFinnish(form.angle, "luonnollinen silmien tasolta kuvattu rajaus");
  const realism = cleanFinnish(form.realism, "realistinen ihon tekstuuri, uskottavat mittasuhteet ja luonnollinen mittakaava");
  const mood = cleanFinnish(form.mood, "lämmin, rauhallinen ja helposti lähestyttävä");
  const clothing = cleanFinnish(form.clothing, "luonnollinen vaatetus ja stailaus");
  const colors = cleanFinnish(form.colors, "luonnolliset ja realistiset sävyt");

  const finnish = `Luo erittäin realistinen ja luonnollisen näköinen kuva, jossa näkyy ${subject} ympäristössä: ${environment}. Kuvan tulee tuntua aidolta kameralla otetulta hetkeltä, ei ylisilotellulta mainoskuvalta. Käytä valaistuksena: ${lighting}. Kameratyyli: ${camera}. Kuvakulma ja rajaus: ${angle}. Tunnelma: ${mood}. Vaatetus ja ulkoasu: ${clothing}. Sävymaailma: ${colors}. Painota ${realism}. Vältä muovista ihoa, ylisiloteltua AI-ilmettä, vääristyneitä käsiä, ylimääräisiä sormia, epärealistisia mittasuhteita ja liian täydellistä stock photo -tunnelmaa.`;

  return appendFinnishVisualGuidance(finnish);
};

export const initialVideoForm = {
  start: "",
  action: "",
  cameraMove: "",
  characterMove: "",
  emotion: "",
  lighting: "",
  mood: "",
  realism: "",
  locked: "",
};

export const buildVideoPrompt = (form) => {
  const primaryInput = getPrimaryUserInput(form, ["start", "action", "characterMove", "mood", "locked"], false);
  const start = cleanFinnish(form.start || primaryInput, "kohde näkyy selkeästi rauhallisessa ja todentuntuisessa aloitustilanteessa");
  const action = cleanFinnish(form.action, "pieni luonnollinen liike, joka tukee tarinaa");
  const cameraMove = cleanFinnish(form.cameraMove, "hidas ja vakaa push-in-kameraliike");
  const characterMove = cleanFinnish(form.characterMove, "pieni uskottava liike ilman äkillisiä muutoksia");
  const emotion = cleanFinnish(form.emotion, "lämmin, luottavainen ja luonnollinen ilme");
  const lighting = cleanFinnish(form.lighting, "pehmeä luonnonvalo, joka pysyy johdonmukaisena koko videon ajan");
  const mood = cleanFinnish(form.mood, "rauhallinen, kutsuva ja realistinen tunnelma");
  const locked = cleanFinnish(form.locked, "henkilön identiteetti, kasvot, vaatetus, tuote, tausta ja tärkeät yksityiskohdat");

  const prompt = `Luo lyhyt, erittäin realistinen AI-video, joka tuntuu aidolta kameralla kuvatulta hetkeltä. Videon alussa: ${start}. Videon aikana: ${action}. Kohteen liike: ${characterMove}. Kameran liike: ${cameraMove}. Ilme ja tunne: ${emotion}. Valaistus: ${lighting}. Tunnelma: ${mood}. Säilytä muuttumattomina: ${locked}. Pidä liike pienenä, uskottavana ja arkisena. Vältä flickeröintiä, morphingia, kasvojen vääristymistä, ylimääräisiä raajoja, robottimaista liikettä, epärealistista kameraliikettä ja tekoälymäistä lopputulosta.`;

  return appendFinnishVisualGuidance(prompt);
};